import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { BreadcrumbGenerator, type BreadcrumbItem } from "@/lib/breadcrumbs/BreadcrumbGenerator";

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  autoGenerate?: boolean;
  pathname?: string;
  className?: string;
}

/**
 * Breadcrumbs Navigation Component
 * 
 * Provides hierarchical navigation with semantic HTML and ARIA labels.
 * Supports both manual items and auto-generation from URL path.
 * 
 * Features:
 * - Semantic HTML (nav, ol, li)
 * - ARIA labels for accessibility
 * - All items clickable except current page
 * - Responsive design with Tailwind CSS
 * 
 * @example
 * // Auto-generate from current path
 * <Breadcrumbs autoGenerate />
 * 
 * // Manual items
 * <Breadcrumbs items={[
 *   { name: 'Home', url: '/' },
 *   { name: 'Treatments', url: '/treatments/' },
 *   { name: 'Dental', url: '/treatments/dental/' }
 * ]} />
 */
export function Breadcrumbs({
  items,
  autoGenerate = false,
  pathname,
  className,
}: BreadcrumbsProps) {
  // Determine breadcrumb items
  let breadcrumbItems: BreadcrumbItem[] = [];

  if (items) {
    breadcrumbItems = items;
  } else if (autoGenerate) {
    const path = pathname || window.location.pathname;
    breadcrumbItems = BreadcrumbGenerator.generateFromPath(path);
  }

  // Don't render if no items or only home
  if (breadcrumbItems.length === 0 || breadcrumbItems.length === 1) {
    return null;
  }

  return (
    <nav
      aria-label="breadcrumb"
      className={cn("mb-4 md:mb-6", className)}
    >
      <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isFirst = index === 0;

          return (
            <li
              key={item.url}
              className="flex items-center gap-2"
            >
              {/* Separator (not for first item) */}
              {!isFirst && (
                <ChevronRight
                  className="h-4 w-4 flex-shrink-0"
                  aria-hidden="true"
                />
              )}

              {/* Breadcrumb item */}
              {isLast ? (
                // Current page - not clickable
                <span
                  className="font-medium text-foreground"
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                // Parent pages - clickable
                <Link
                  to={item.url}
                  className="hover:text-foreground transition-colors"
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
